import axios from 'axios';

import {
  CreateOrUpdateLSCustomerAccountResponseData,
  CustomerAccount,
  CustomerAccountResponse,
  CustomerAccountResponseData,
  LSCustomerAccount,
} from 'root/apps/QuoteMachineIntegration/state';
import Modules from 'root/apps/shared_modules/Modules';
import { datadogRum } from 'root/datadog/datadogRum';

import { LS_INVOICING_API_ROOT } from '../constants';
import { formatRefId } from '../utils/referenceHelpers';
import { getCreditAccount } from './v1/creditAccountAdapter';

// Simple cache to prevent duplicate API calls
const customerAccountCache = new Map<string, { data: CustomerAccount; timestamp: number }>();
const CACHE_DURATION = 5000; // 5 seconds cache

// Track pending requests to prevent duplicates
const pendingRequests = new Map<string, Promise<CustomerAccount>>();

// Parse getCustomerAccount response to properly format Money amounts
const parseCustomerAccount = ({
  pendingPaymentAmount,
  ...rest
}: CustomerAccountResponseData): CustomerAccount => ({
  ...rest,
  overdueBalance: rest.overdueBalance ? parseFloat(rest.overdueBalance) : null,
  owingBalance: rest.owingBalance ? parseFloat(rest.owingBalance) : null,
  depositBalance: rest.depositBalance ? parseFloat(rest.depositBalance) : null,
  creditLimit: parseFloat(rest.creditLimit),
  availableAmountFromCreditLimit: parseFloat(rest.availableAmountFromCreditLimit),
  totalProcessing: pendingPaymentAmount ? parseFloat(pendingPaymentAmount) : null,
});

// Parse createOrUpdateCustomerCreditAccount response
const parseLSCustomerAccount = (
  response: CreateOrUpdateLSCustomerAccountResponseData
): LSCustomerAccount => ({
  CreditAccount: {
    balance: response.Customer.CreditAccount.balance,
    creditAccountRef: response.Customer.CreditAccount.creditAccountID,
    creditLimit: response.Customer.CreditAccount.creditLimit,
    creditAccountTimeStamp: response.Customer.CreditAccount.timeStamp,
  },
  archived: response.Customer.archived,
  contactRef: response.Customer.contactID,
  createTime: response.Customer.createTime,
  creditAccountRef: response.Customer.creditAccountID,
  customerID: response.Customer.customerID,
  firstName: response.Customer.firstName,
  lastName: response.Customer.lastName,
  customerTimeStamp: response.Customer.timeStamp,
});

const Service = {
  // Gets Invoicing customer account
  getCustomerAccount: (creditAccountID: string, creditLimit: string) => {
    const cacheKey = `${creditAccountID}-${creditLimit}`;
    const now = Date.now();

    // Check cache first
    const cached = customerAccountCache.get(cacheKey);
    if (cached && now - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    // Check if there's already a pending request for this key
    const pendingRequest = pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    // Create new request
    const newRequest = (async () => {
      try {
        let parsedData: CustomerAccount;

        if (Modules.hasModule('invoicing.api.v1.credit_account_balance')) {
          parsedData = await getCreditAccount(creditAccountID, creditLimit);
        } else {
          const { data } = await axios.get<CustomerAccountResponse>(
            `/admin/invoicing/ls-customer-accounts/${creditAccountID}?credit_limit=${creditLimit}`
          );
          parsedData = parseCustomerAccount(data.attributes);
        }

        // Cache the result
        customerAccountCache.set(cacheKey, { data: parsedData, timestamp: now });

        return parsedData;
      } catch (error) {
        if (!Modules.hasModule('invoicing.api.v1.credit_account_balance')) {
          datadogRum.addError(error, {
            creditAccountID,
            message: 'Failed to fetch invoicing/ls-customer-accounts',
          });
        }
        throw error;
      } finally {
        // Remove from pending requests
        pendingRequests.delete(cacheKey);
      }
    })();

    // Store the pending request
    pendingRequests.set(cacheKey, newRequest);

    return newRequest;
  },

  // Gets LS customer, response contains credit account details (loads CreditAccount relation)
  getLsCustomerAccount: async (customerID: string) => {
    const accountID = window.merchantos.session.getSystemCustomerID();

    try {
      const { data } = await axios.get(
        `/API/V3/Account/${accountID}/Customer/${customerID}.json?load_relations=["CreditAccount"]`
      );
      return data;
    } catch (error) {
      datadogRum.addError(error, {
        accountID,
        customerID,
        message: 'Unable to fetch customer credit account',
      });
      throw error;
    }
  },

  // Creates or updates LS customer credit account, response contains credit account details (loads CreditAccount relation)
  createOrUpdateCustomerCreditAccount: async (customerID: string, creditLimitAmount: string) => {
    const accountID = window.merchantos.session.getSystemCustomerID();

    try {
      const { data } = await axios.put(
        `/API/V3/Account/${accountID}/Customer/${customerID}.json?load_relations=["CreditAccount"]`,
        {
          CreditAccount: {
            creditLimit: parseFloat(creditLimitAmount),
          },
        }
      );

      const result = parseLSCustomerAccount(data);

      datadogRum.addAction('credit_account_created_or_updated', {
        accountID,
        customerID,
        creditAccountID: result.CreditAccount.creditAccountRef,
        creditLimit: creditLimitAmount,
        message: 'Credit account created or updated',
      });

      // Sync the credit limit to the invoicing API
      if (Modules.hasModule('invoicing.api.v1.credit_account_balance')) {
        const creditLimitCents = Math.round(parseFloat(creditLimitAmount) * 100);
        const creditAccountRef = formatRefId(String(result.CreditAccount.creditAccountRef));
        await axios.put(
          `${LS_INVOICING_API_ROOT}/credit-accounts/${creditAccountRef}/credit-limit`,
          { creditLimit: creditLimitCents }
        );
        await axios.post(
          `${LS_INVOICING_API_ROOT}/credit-accounts/${creditAccountRef}/balances/refresh`
        );
      }

      return result;
    } catch (error) {
      datadogRum.addError(error, {
        accountID,
        customerID,
        creditLimitAmount,
        message: 'Failed to create or update customer credit account',
      });
      throw error;
    }
  },

  // Ensures a credit account exists for the customer, creating one with $0 limit if needed
  // Used by return/deposit/refund flows that don't modify existing credit limits
  ensureCreditAccountExists: async (customerID: string) => {
    // Check if customer already has a credit account
    const customerData = await Service.getLsCustomerAccount(customerID);
    const existingCreditAccountID = customerData.Customer.creditAccountID;

    if (existingCreditAccountID && existingCreditAccountID > 0) {
      // Account already exists - return it without calling PUT
      return parseLSCustomerAccount(customerData);
    }

    // Account doesn't exist - create one with $0 limit
    return Service.createOrUpdateCustomerCreditAccount(customerID, '0');
  },

  // Report account balance drift between LSR and Invoicing systems
  reportBalanceDrift: async (creditAccountID: string) => {
    try {
      await axios.post(`/admin/invoicing/v1/credit-accounts/${creditAccountID}/identify-drift`);

      datadogRum.addAction('balance_drift_detected', {
        creditAccountID,
        message: 'Balance drift detected between LSR and Invoicing',
      });
    } catch (error) {
      // Silent failure - don't disrupt user experience
      datadogRum.addError(error, {
        creditAccountID,
        message: 'Failed to report balance drift',
      });
    }
  },
};

export default Service;
